import { keyCandidates } from "./joinRows.js";
import { findLabelColumn } from "./geo.js";

/* Turning the codes in a register into the names people use.
 *
 * The VIDE risk register files against School as a code: AAS, AHS, CTEC. A
 * card that says AHS is no use in a meeting, and a list of codes typed into
 * this app would be wrong the first time somebody added a school.
 *
 * The client's schools layer already carries both the code and the name, so
 * it is the authority on both. Which of its columns holds the code is worked
 * out rather than configured: every column that could be a key is tried and
 * the one that accounts for the most of the register's own values wins.
 *
 * A code the layer does not carry is not translated and not guessed at. Some
 * of what the register files against is not a school, and drawing those as
 * schools would be a quiet lie.
 */

export const normalise = (value) => String(value ?? "").trim().toUpperCase();

export function siteNameMap({ columns = [], rows = [], sites = [] } = {}) {
  const wanted = new Set(sites.map(normalise).filter(Boolean));
  if (!wanted.size || !rows.length) return null;

  const labelKey = findLabelColumn(columns, rows)?.key ?? null;

  /* Which column holds the code is decided by how many of the register's own
     values it accounts for. The names themselves cover every row in the layer,
     not only the matched ones: the layer is the authority on which schools
     exist, and a school with nothing filed against it still has to appear or
     the board quietly drops it. */
  let best = null;
  for (const col of keyCandidates(columns, rows)) {
    const names = new Map();
    let hits = 0;
    for (const row of rows) {
      const code = normalise(row[col.key]);
      if (!code || names.has(code)) continue;
      if (wanted.has(code)) hits += 1;
      const label = labelKey ? String(row[labelKey] ?? "").trim() : "";
      names.set(code, label || code);
    }
    if (!best || hits > best.matched) best = { column: col, names, matched: hits };
  }

  if (!best || best.matched === 0) return null;

  return {
    ...best,
    /* What the register files against that this layer has never heard of.
       Reported, never translated, never drawn as though it were a school. */
    unmatched: [...wanted].filter((code) => !best.names.has(code)).sort(),
  };
}
